import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  ChartOptions,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { DescuentoPorMes } from "../../../services/analisisFinanzas";
import { useEstadisticasNumericas } from "../../../hooks/estadisticasNumericas";
import { formatearAnoMes } from "../../../utils/dateUtils";
import { currencyFormat } from "../../../utils/numberUtils";
import { divertidogsTheme } from "../../../styles/divertidogs-theme";
import styled from "styled-components";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

interface DescuentosPorMesChartProps {
  descuentosPorMes: DescuentoPorMes[];
}

function DescuentosPorMesChart({ descuentosPorMes }: DescuentosPorMesChartProps) {
  const mainColor = divertidogsTheme.colors.main;
  const promedioColor = divertidogsTheme.colors.info;
  const minimoColor = divertidogsTheme.colors.error;
  const maximoColor = divertidogsTheme.colors.success;
  const estadisticas = useEstadisticasNumericas(
    descuentosPorMes,
    (item) => item.descuentos
  );

  const chartData = useMemo(() => {
    const labels = descuentosPorMes.map((item) =>
      formatearAnoMes(new Date(item.anoMes))
    );
    const descuentos = descuentosPorMes.map((item) => item.descuentos);

    return {
      labels,
      datasets: [
        {
          label: "Descuentos (COP)",
          data: descuentos,
          borderColor: mainColor,
          backgroundColor: `${mainColor}20`,
          fill: true,
          pointRadius: 5,
          pointBackgroundColor: mainColor,
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
        },
      ],
    };
  }, [mainColor, descuentosPorMes]);

  const chartOptions: ChartOptions<"line"> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "Descuentos mensuales",
        font: {
          size: 16,
          weight: "bold" as const,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<"line">) {
            const valor = context.parsed.y;
            return typeof valor === "number"
              ? `${context.dataset.label}: ${currencyFormat(valor)}`
              : String(context.dataset.label ?? "");
          },
        },
      },
      annotation: estadisticas && descuentosPorMes.length > 1
        ? {
            annotations: {
              promedio: {
                type: "line",
                yMin: estadisticas.promedio,
                yMax: estadisticas.promedio,
                borderColor: promedioColor,
                borderDash: [6, 6],
                borderWidth: 2,
                label: {
                  display: true,
                  position: "end",
                  backgroundColor: promedioColor,
                  content: `Prom: ${currencyFormat(estadisticas.promedio)}`,
                },
              },
              minimo: {
                type: "line",
                yMin: estadisticas.minimo,
                yMax: estadisticas.minimo,
                borderColor: minimoColor,
                borderDash: [2, 4],
                borderWidth: 2,
                label: {
                  display: true,
                  position: "end",
                  backgroundColor: minimoColor,
                  content: `Min: ${currencyFormat(estadisticas.minimo)}`,
                },
              },
              maximo: {
                type: "line",
                yMin: estadisticas.maximo,
                yMax: estadisticas.maximo,
                borderColor: maximoColor,
                borderDash: [2, 4],
                borderWidth: 2,
                label: {
                  display: true,
                  position: "end",
                  backgroundColor: maximoColor,
                  content: `Max: ${currencyFormat(estadisticas.maximo)}`,
                },
              },
            },
          }
        : undefined,
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return currencyFormat(Number(value));
          },
        },
      },
    },
  }), [
    estadisticas,
    promedioColor,
    minimoColor,
    maximoColor,
    descuentosPorMes.length,
  ]);

  return (
    <ChartContainer>
      <Line data={chartData} options={chartOptions} />
    </ChartContainer>
  );
}

const ChartContainer = styled.div`
  height: 100%;
  width: 100%;
`;

export default React.memo(DescuentosPorMesChart);
